import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import initBootstrapComponents from '../../utils/bootstrap';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Modal from 'react-bootstrap/Modal';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import axios from "axios";
import { Riple } from "react-loading-indicators";
import { useBasket } from '../../context/BasketContext';
import OrderDetails from "../../components/OrderDetails/OrderDetails";
import { API_BASE_URL } from '../../constants/api';

const EventAvailability = () => {

    const { eventId } = useParams();

    const { addToBasket, basket } = useBasket();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedTimes, setSelectedTimes] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [filteredTimeSlots, setFilteredTimeSlots] = useState([]);

    const [eventDetails, setEventDetails] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);

    // Load saved times from localStorage when component mounts
    useEffect(() => {
        const savedTimes = localStorage.getItem('selectedTimes');
        if (savedTimes) {
            const parsedTimes = JSON.parse(savedTimes);
            setSelectedTimes(parsedTimes);
            
            // If we have saved times, set the date to the first time slot's date
            if (parsedTimes.length > 0) {
                const firstTimeDate = new Date(parsedTimes[0].start_at);
                setSelectedDate(firstTimeDate);
            }
        }
    }, []);

    // Save times to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('selectedTimes', JSON.stringify(selectedTimes));
    }, [selectedTimes]);

    // Replace PHP $siteUrl with a constant or environment variable
    const siteUrl = process.env.REACT_APP_SITE_URL || '/';

    useEffect(() => {
        // Initialize Bootstrap components when component mounts
        initBootstrapComponents();
        fetchEventDetails();
    }, []);

    // Pre-select slots already in the basket for this event
    useEffect(() => {
        if (basket?.slots?.length > 0) {
            const eventSlots = basket.slots.filter(slot => slot.event_id === eventId);
            if (eventSlots.length > 0) {
                setSelectedTimes(eventSlots);
                if (eventSlots[0]?.date) {
                    setSelectedDate(new Date(eventSlots[0].date));
                }
            }
        }
    }, []);

    useEffect(() => {
        if (availableDates.length > 0 && !selectedDate) {
            handleDateSelect(availableDates[0]);
        }
    }, [availableDates]);

    // Fetch slots for the selected day
    useEffect(() => {
        if (selectedDate) {
            fetchSlots(selectedDate);
        }
    }, [selectedDate]);

    useEffect(() => {
        if (selectedDate && timeSlots.length > 0) {
            filterTimeSlotsByDate(selectedDate);
        }
    }, [selectedDate, timeSlots]);

    const fetchEventDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/` + eventId + `/details?key=7678356c642c91a255471d162205e0f3`);
            const event = response.data.data;
            setEventDetails(event);
            // Generate array of dates between start_at and end_at
            const dates = generateDateRange(new Date(event.start_at), new Date(event.end_at));

            setAvailableDates(dates);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching event details:', error);
        }
    };

    const fetchSlots = async (date) => {
        setSlotsLoading(true);
        try {
            const dateParam = formatDateParam(date);
            const response = await axios.get(`${API_BASE_URL}/api/events/` + eventId + `/slot-bookings?key=7678356c642c91a255471d162205e0f3&format=vehicles&start_at=${dateParam}&end_at=${dateParam}`);

            // Flatten the per-vehicle slots into a single list and dedupe by slot id
            const slotsById = new Map();
            Object.values(response.data.data).forEach(vehicleData => {
                Object.values(vehicleData.slots || {}).forEach(slot => {
                    if (!slotsById.has(slot.id)) {
                        slotsById.set(slot.id, slot);
                    }
                });
            });

            const slots = Array.from(slotsById.values())
                .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

            setTimeSlots(slots);
        } catch (error) {
            console.error('Error fetching slots:', error);
        } finally {
            setSlotsLoading(false);
        }
    }

    const formatDateParam = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const generateDateRange = (startDate, endDate) => {
        const dates = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const isSameDay = (date1, date2) => {
        return date1.getFullYear() === date2.getFullYear() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getDate() === date2.getDate();
    };

    const filterTimeSlotsByDate = (selectedDate) => {
        const filtered = timeSlots.filter(slot => {
            const slotDate = new Date(slot.start_at);
            return isSameDay(slotDate, selectedDate);
        });
        
        // Sort time slots by start time
        const sorted = filtered.sort((a, b) => {
            return new Date(a.start_at) - new Date(b.start_at);
        });
        
        setFilteredTimeSlots(sorted);
        
        // Filter selected times to only include those for the current date
        // const currentDateTimes = selectedTimes.filter(time => {
        //     const timeDate = new Date(time.start_at);
        //     return isSameDay(timeDate, selectedDate);
        // });
        
        // setSelectedTimes(currentDateTimes);
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    const handleTimeSelect = (time) => {
        setSelectedTimes(prevTimes => {
            // Check if the time is already selected
            const isSelected = prevTimes.some(t => t.id === time.id);
            
            if (isSelected) {
                // If selected, remove it from both selectedTimes and basket
                const updatedTimes = prevTimes.filter(t => t.id !== time.id);
                updateBasketWithTimes(updatedTimes);
                return updatedTimes;
            } else {
                // If not selected, add it to both selectedTimes and basket
                const updatedTimes = [...prevTimes, time];
                updateBasketWithTimes(updatedTimes);
                return updatedTimes;
            }
        });
    };

    const updateBasketWithTimes = (times) => {
        const slots = times.map(time => ({
            id: time.id,
            start_at: time.start_at,
            event_id: time.event_id,
            date: new Date(time.start_at)
        }));

        addToBasket({
            event: {
                name: eventDetails?.name,
                id: eventDetails?.id
            },
            slots: slots
        });
    };

    const formatTimeSlot = (dateTimeString) => {
        return new Date(dateTimeString).toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).toLowerCase();
    };

    const handleGoToCheckout = () => {
        navigate('/client-details-form');
    };

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />

            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-87" id="book-vehicle">
                    <div className="container">
                        <h1 className="text-primary mb-4 mb-lg-5">
                            Book a test drive
                        </h1>

                        <div className="row row-gap-4 row-gap-lg-0 gx-xxl-5">
                            <div className="col-lg-6">
                                <div className="bg-motability-white border border-motability-light-blue rounded-4 p-4 p-lg-32 pb-lg-5">
                                    {eventDetails && (
                                        <h5 className="mb-4 mb-lg-32">
                                            {eventDetails.name}
                                        </h5>
                                    )}

                                    {/* Date Selection */}
                                    <div className="mb-4 mb-lg-5">
                                        <p className="text-primary fw-semibold mb-3">
                                            Choose a date
                                        </p>
                                        <div className="select-buttons d-flex align-items-center flex-wrap column-gap-2 row-gap-3">
                                            {availableDates.map((date) => (
                                                <button
                                                    key={date.toISOString()}
                                                    type="button"
                                                    className={`select-btn btn ${
                                                        selectedDate && selectedDate.toDateString() === date.toDateString()
                                                            ? 'btn-primary text-white'
                                                            : 'bg-motability-light-orange'
                                                    } rounded-4 d-flex align-items-center justify-content-center p-3 shadow-none`}
                                                    onClick={() => handleDateSelect(date)}
                                                >
                                                    {formatDate(date)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {(loading || slotsLoading) && <Riple color="#0052BE" size="medium" text="" textColor="" />}

                                    {/* Time Slots - Only show if date is selected */}
                                    {selectedDate && (
                                        <div>
                                            <p className="text-primary fw-semibold mb-3">
                                                Choose a time
                                            </p>
                                            {filteredTimeSlots.length > 0 ? (
                                                <div className="select-buttons d-flex align-items-center flex-wrap column-gap-2 row-gap-3" id="select-time-slots">
                                                    {filteredTimeSlots.map((time) => (
                                                        <button
                                                            key={time.id}
                                                            type="button"
                                                            title={formatTimeSlot(time.start_at)}
                                                            className={`select-btn btn ${
                                                                selectedTimes.some(t => t.id === time.id)
                                                                    ? 'btn-primary text-white'
                                                                    : 'bg-motability-light-orange'
                                                            } rounded-pill d-flex align-items-center justify-content-center p-0 w-84 h-34 fs-15 shadow-none`}
                                                            onClick={() => handleTimeSelect(time)}
                                                        >
                                                            {formatTimeSlot(time.start_at)}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p>No available time slots for this date.</p>
                                            )}

                                            <div>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary px-29 mt-4 mt-lg-32"
                                                    onClick={(e) => {
                                                        if (selectedTimes.length === 0) {
                                                            e.preventDefault();
                                                            return;
                                                        }
                                                        setShowModal(true);
                                                    }}
                                                    disabled={selectedTimes.length === 0}
                                                    style={{
                                                        opacity: selectedTimes.length === 0 ? 0.6 : 1,
                                                        cursor: selectedTimes.length === 0 ? 'not-allowed' : 'pointer',
                                                        pointerEvents: selectedTimes.length === 0 ? 'none' : 'auto'
                                                    }}
                                                >
                                                    Book your test drive
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <OrderDetails />

                        </div>

                        <div className="d-flex flex-column align-items-start mt-4 mt-lg-64">
                            <Link to="/" className="d-inline-block fw-medium text-decoration-none">
                                ‹ <span className="d-inline-block text-decoration-underline">Back to the event page</span>
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                aria-labelledby="add-slot-to-basket-modal"
            >
                <Modal.Header className="pt-64 pb-5 border-0 justify-content-center">
                    <h4 className="text-primary text-center mb-0" id="add-slot-to-basket-modal">
                        Add slot to Basket
                    </h4>
                </Modal.Header>

                <Modal.Footer className="gap-1 gap-lg-39 px-3 pt-0 pb-64 justify-content-center border-0">
                    <button 
                        type="button" 
                        className="btn btn-primary m-0 px-lg-29"
                        onClick={handleGoToCheckout}
                    >
                        Go to Checkout
                    </button>

                    <button 
                        type="button" 
                        className="btn btn-outline-primary fw-normal m-0 px-lg-29"
                        onClick={() => setShowModal(false)}
                    >
                        Continue booking
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EventAvailability;
